import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Mail, Phone, MapPin, Building2, Users, Gift,
  ChevronRight, ChevronLeft, Check, Loader2, Sparkles,
  CreditCard, Star, Clock, DollarSign, X, CheckCircle2,
  Target, TrendingUp, Zap, AlertTriangle, Eye, Rocket,
  UserCheck, Headphones, Globe, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Business Categories with icons
const BUSINESS_CATEGORIES = [
  { id: 'local_business', label: 'Local Business Owner', icon: '🏪', image: 'https://involve-me.imgix.net/uploads/assets/2b02c279-aa24-4353-9f28-16f108f30181.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'ecommerce', label: 'Online Business / E-commerce', icon: '🛒', image: 'https://involve-me.imgix.net/uploads/assets/ddb15cab-30d2-406a-8e9b-0b214ce66578.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'mortgage', label: 'Mortgage Loan Officer', icon: '🏠', image: 'https://involve-me.imgix.net/uploads/assets/c899906e-33cd-4443-9968-71cc4842fdaf.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'real_estate', label: 'Real Estate Agent / Consultant', icon: '🏢', image: 'https://involve-me.imgix.net/uploads/assets/0cd23fd0-89da-498b-9658-02be1be80ee4.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'trainer', label: 'Business Trainer / Coach', icon: '🎯', image: 'https://involve-me.imgix.net/uploads/assets/ee17522a-1d83-4f76-a596-c0442f0b7581.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'solopreneur', label: 'Solopreneur', icon: '💼', image: 'https://involve-me.imgix.net/uploads/assets/302f55de-3135-47ad-bd09-6a49bde02fdd.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'law_firm', label: 'Law Firm Owner', icon: '⚖️', image: 'https://involve-me.imgix.net/uploads/assets/c3a22e29-0e76-4259-97e1-1f022042cab5.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'accountant', label: 'Accountant / Bookkeeper', icon: '📊', image: 'https://involve-me.imgix.net/uploads/assets/88efe70a-9c5e-41ae-a372-10022318dd84.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'trucking', label: 'Trucking Company', icon: '🚚', image: 'https://involve-me.imgix.net/uploads/assets/5cb9e21f-f5e7-456f-b3fd-8d8265f70c15.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'automotive', label: 'Automotive Dealership', icon: '🚗', image: 'https://involve-me.imgix.net/uploads/assets/6f708a6a-0ded-4eba-813f-c5ee44ab8adb.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'agency', label: 'Online Agency', icon: '💻', image: 'https://involve-me.imgix.net/uploads/assets/3d91ae12-d8bb-4c8e-8c66-661af7a23992.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'freelance', label: 'Freelancer / Gig Worker', icon: '✨', image: 'https://involve-me.imgix.net/uploads/assets/33834e5c-2df7-4f0b-bea9-1a60fbbeae81.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'influencer', label: 'Influencer / Publisher', icon: '📱', image: 'https://involve-me.imgix.net/uploads/assets/a650636c-fa47-45cf-99d5-ff6008f07931.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'affiliate', label: 'Affiliate Marketer', icon: '🔗', image: 'https://involve-me.imgix.net/uploads/assets/1a125a8d-7c95-4150-8786-27a85433c476.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'talent', label: 'Model / Actor / Talent', icon: '🌟', image: 'https://involve-me.imgix.net/uploads/assets/9cefcb8e-b0ae-41f8-a8aa-35788512880c.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'musician', label: 'Musician / Entertainer', icon: '🎵', image: 'https://involve-me.imgix.net/uploads/assets/69cb206e-cc15-4785-ac68-739a4224a1e6.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'nonprofit', label: 'Non-Profit Organization', icon: '❤️', image: 'https://involve-me.imgix.net/uploads/assets/fb3efdfc-dd17-4003-96c6-2cd0604b6633.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'construction', label: 'Construction Company', icon: '🏗️', image: 'https://involve-me.imgix.net/uploads/assets/7b40212a-5215-450e-945f-345f8f5e3f89.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'hvac', label: 'HVAC Company', icon: '❄️', image: 'https://involve-me.imgix.net/uploads/assets/56002251-3698-4d9e-ac59-d13a599d4fbe.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'restaurant', label: 'Restaurant', icon: '🍽️', image: 'https://involve-me.imgix.net/uploads/assets/d4265e3e-e0e5-47dd-80a1-ed682639c214.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'manufacturer', label: 'Manufacturer / Wholesale', icon: '🏭', image: 'https://involve-me.imgix.net/uploads/assets/58779d0f-8ef2-4d7a-ba28-f98487899556.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'startup', label: 'Startup / Idea Stage', icon: '🚀', image: 'https://involve-me.imgix.net/uploads/assets/803ee982-3211-495b-a809-d698b0a2da66.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'investor', label: 'Investor / Private Equity', icon: '💰', image: 'https://involve-me.imgix.net/uploads/assets/17a53746-8341-4b84-8a23-82db5462ab6f.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
  { id: 'saas', label: 'SaaS Provider / Agency', icon: '☁️', image: 'https://involve-me.imgix.net/uploads/assets/258cd1df-e622-4a88-9199-349a4a456367.png?q=75&auto=format&dpr=1&fit=crop&ar=1:1&w=192' },
];

// Challenge options based on 9-Step Framework
const CHALLENGE_OPTIONS = [
  { id: 'leads', label: 'Not enough leads or customers', icon: TrendingUp },
  { id: 'marketing', label: 'Marketing not working / inconsistent', icon: Target },
  { id: 'time', label: 'No time to do it all myself', icon: Clock },
  { id: 'technology', label: 'Outdated technology / no CRM', icon: Zap },
  { id: 'brand', label: 'Weak brand awareness', icon: Eye },
  { id: 'competition', label: 'Losing to competition', icon: AlertTriangle },
];

// Vision options
const VISION_OPTIONS = [
  { id: 'leads_daily', label: 'Leads coming in daily', icon: TrendingUp },
  { id: 'brand_everywhere', label: 'My brand everywhere online', icon: Eye },
  { id: 'automated', label: 'Everything automated', icon: Zap },
  { id: 'team_support', label: 'Team handling execution', icon: Users },
  { id: 'revenue_growth', label: '2-3x revenue growth', icon: DollarSign },
  { id: 'freedom', label: 'More time & freedom', icon: Star },
];

// Wizard Steps - Extended with 9-Step Framework + Representative question
const STEPS = {
  WELCOME: 0,
  NAME: 1,
  CONTACT: 2,
  CATEGORY: 3,
  LOCATION: 4,
  // 9-Step Framework Questions
  CHALLENGE: 5,
  WHY_NOW: 6,
  VISION: 7,
  WHATS_MISSING: 8,
  COMMITMENT: 9,
  // Original + New Steps
  OWNERSHIP: 10,
  REPRESENTATIVE: 11,
  REFERRAL: 12,
  VERIFYING: 13,
  APPROVED: 14,
  // Post-Approval Steps (Steps 2-10 in user flow)
  APPLICATION_FEE: 15,      // Step 2: Pay $75
  BOOK_CALL: 16,            // Step 3: Book Closing Call
  WE_CALL_YOU: 17,          // Step 4: We Call You
  REVIEW_PRESENTATION: 18,  // Step 5: Review Presentation & Directory
  FUNDING_OPTIONS: 19,      // Step 6: Choose Funding Option
  VIEW_PRICING: 20,         // Step 7: View Pricing
  COMPLETE_ONBOARDING: 21,  // Step 8: Complete Onboarding
  LOGIN_INFO: 22,           // Step 9: Login Info Sent
  PLACE_ORDER: 23,          // Step 10: Place 1st Order
  COMPLETE: 24
};

// Storage key for persistence
const STORAGE_KEY = 'oneforce_preapproval_progress';

const PreApprovalWizard = ({ isOpen, onClose, onComplete, openPodPanel, embedded = false }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    category: '',
    categoryLabel: '',
    zipCode: '',
    challenge: '',
    whyNow: '',
    vision: [],
    whatsMissing: '',
    commitmentLevel: null,
    isOwner: null,
    hasRepresentative: null,
    representativeName: '',
    wasReferred: null,
    referrerName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [preApprovalData, setPreApprovalData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [selectedFundingOption, setSelectedFundingOption] = useState(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [hasReviewedPresentation, setHasReviewedPresentation] = useState(false);
  const [stepsUnlocked, setStepsUnlocked] = useState(false);

  // Load saved progress on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.step > STEPS.NAME && parsed.formData?.email) {
          setSavedProgress(parsed);
          setShowResumePrompt(true);
          // Check if they've already reviewed presentation
          if (parsed.hasReviewedPresentation) {
            setHasReviewedPresentation(true);
            setStepsUnlocked(true);
          }
        }
      } catch (e) {
        console.error('Error parsing saved progress:', e);
      }
    }
  }, []);

  // Save progress whenever step or formData changes
  useEffect(() => {
    if (currentStep > STEPS.NAME && formData.email) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step: currentStep,
        formData,
        hasReviewedPresentation,
        stepsUnlocked,
        timestamp: Date.now()
      }));
    }
  }, [currentStep, formData, hasReviewedPresentation, stepsUnlocked]);

  // Check for payment return from GHL - user is redirected back after paying
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const sessionId = urlParams.get('session_id');
    const packageId = urlParams.get('package');
    
    // Check if user is returning from GHL payment link
    // GHL may add params like ?status=success or just redirect back
    const ghlSuccess = urlParams.get('status') === 'success' || urlParams.get('paid') === 'true';
    
    // Also check if we have saved progress indicating they were at payment step
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // If they were at APPLICATION_FEE step and are now returning, assume payment completed
        if (parsed.step === STEPS.APPLICATION_FEE && parsed.formData?.email) {
          // They're returning - move them to BOOK_CALL (Step 3)
          setFormData(parsed.formData);
          setCurrentStep(STEPS.BOOK_CALL);
          localStorage.setItem('applicationFeePaid', 'true');
          // Update the saved progress
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...parsed,
            step: STEPS.BOOK_CALL,
            applicationFeePaid: true
          }));
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      } catch (e) {
        console.error('Error checking saved progress:', e);
      }
    }
    
    if (paymentSuccess && sessionId) {
      window.history.replaceState({}, '', window.location.pathname);
      pollPaymentStatus(sessionId, packageId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, packageId) => {
    setIsLoading(true);
    let attempts = 0;
    const maxAttempts = 10;
    
    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus({ success: true, packageId });
          
          const savedData = localStorage.getItem('preApprovalFormData');
          if (savedData) {
            setFormData(JSON.parse(savedData));
            localStorage.removeItem('preApprovalFormData');
          }
          
          if (packageId === 'application_fee') {
            localStorage.setItem('applicationFeePaid', 'true');
            setCurrentStep(STEPS.BOOK_CALL);
          } else {
            localStorage.setItem('fundingFeePaid', 'true');
            setCurrentStep(STEPS.LOGIN_INFO);
          }
          setIsLoading(false);
          return;
        }
        
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Payment status error:', error);
        setIsLoading(false);
      }
    };
    
    poll();
  };

  const handleResumeProgress = () => {
    if (savedProgress) {
      setFormData(savedProgress.formData);
      setCurrentStep(savedProgress.step);
      setShowResumePrompt(false);
    }
  };

  const handleStartFresh = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSavedProgress(null);
    setShowResumePrompt(false);
    setCurrentStep(STEPS.WELCOME);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategorySelect = (category) => {
    setFormData(prev => ({
      ...prev,
      category: category.id,
      categoryLabel: category.label
    }));
  };

  const handleVisionToggle = (visionId) => {
    setFormData(prev => ({
      ...prev,
      vision: prev.vision.includes(visionId)
        ? prev.vision.filter(v => v !== visionId)
        : [...prev.vision, visionId]
    }));
  };

  const nextStep = () => setCurrentStep(prev => prev + 1);
  const prevStep = () => setCurrentStep(prev => prev - 1);

  const startVerification = async () => {
    setCurrentStep(STEPS.VERIFYING);
    setVerificationProgress(0);
    
    const interval = setInterval(() => {
      setVerificationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 800);

    try {
      const response = await fetch(`${API_URL}/api/pre-approval/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          category: formData.category,
          zip_code: formData.zipCode,
          is_owner: formData.isOwner,
          has_representative: formData.hasRepresentative,
          representative_name: formData.representativeName,
          referred_by: formData.wasReferred ? 'yes' : 'no',
          referrer_name: formData.referrerName,
          challenge: formData.challenge,
          why_now: formData.whyNow,
          vision: formData.vision,
          whats_missing: formData.whatsMissing,
          commitment_level: formData.commitmentLevel
        })
      });
      
      const data = await response.json();
      setPreApprovalData(data);
      
      setTimeout(() => {
        clearInterval(interval);
        setVerificationProgress(100);
        setTimeout(() => setCurrentStep(STEPS.APPROVED), 500);
      }, 10000);
      
    } catch (error) {
      console.error('Pre-approval error:', error);
      clearInterval(interval);
      setVerificationProgress(100);
      setTimeout(() => setCurrentStep(STEPS.APPROVED), 500);
    }
  };

  const initiatePayment = async (packageId) => {
    setIsLoading(true);
    localStorage.setItem('preApprovalFormData', JSON.stringify(formData));
    
    // Mark which payment is pending so we can detect return
    if (packageId === 'application_fee') {
      localStorage.setItem('pendingApplicationFee', 'true');
    } else {
      localStorage.setItem('pendingFundingPayment', 'true');
    }
    
    try {
      // Use GHL Payment Link with pre-filled customer data
      const GHL_PAYMENT_LINK = 'https://link.oneforcelive.io/payment-link/6994ee5d88a3f0c8de8078d0';
      
      // Build URL parameters to pre-fill customer info
      const params = new URLSearchParams({
        first_name: formData.firstName || '',
        last_name: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || '',
        name: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
      });
      
      // Redirect to GHL payment link with pre-filled data
      const paymentUrl = `${GHL_PAYMENT_LINK}?${params.toString()}`;
      window.location.href = paymentUrl;
      
    } catch (error) {
      console.error('Payment error:', error);
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    localStorage.removeItem(STORAGE_KEY);
    onComplete?.(formData);
    onClose?.();
    openPodPanel?.();
  };

  const handleSkipToFreeTrial = () => {
    onClose?.();
    openPodPanel?.();
  };

  // Track abandoned lead when wizard is closed early
  const trackAbandonedLead = useCallback(async () => {
    // Only track if we have at least an email and they haven't completed
    if (formData.email && currentStep > STEPS.CONTACT_INFO && currentStep < STEPS.COMPLETE) {
      try {
        await fetch(`${API_URL}/api/pre-approval/abandoned`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: formData.firstName || '',
            last_name: formData.lastName || '',
            email: formData.email,
            phone: formData.phone || '',
            category: formData.category || '',
            zip_code: formData.zipCode || '',
            is_owner: formData.isOwner ?? true,
            has_representative: formData.hasRepresentative || false,
            representative_name: formData.representativeName || '',
            referrer_name: formData.referrerName || '',
            challenge: formData.challenge || '',
            why_now: formData.whyNow || '',
            vision: formData.vision || [],
            whats_missing: formData.whatsMissing || '',
            commitment_level: formData.commitmentLevel,
            step_abandoned: `Step ${currentStep}`
          })
        });
        console.log('Abandoned lead tracked');
      } catch (error) {
        console.error('Failed to track abandoned lead:', error);
      }
    }
  }, [formData, currentStep]);

  // Track abandoned lead when closing the wizard
  const handleClose = useCallback(async () => {
    await trackAbandonedLead();
    onClose?.();
  }, [trackAbandonedLead, onClose]);

  // Track on page unload (when user leaves the page)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formData.email && currentStep > STEPS.CONTACT_INFO && currentStep < STEPS.COMPLETE) {
        // Use sendBeacon for reliable delivery on page unload
        navigator.sendBeacon(
          `${API_URL}/api/pre-approval/abandoned`,
          JSON.stringify({
            first_name: formData.firstName || '',
            last_name: formData.lastName || '',
            email: formData.email,
            phone: formData.phone || '',
            category: formData.category || '',
            zip_code: formData.zipCode || '',
            step_abandoned: `Step ${currentStep}`
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, currentStep]);

  if (!isOpen && !embedded) return null;

  const totalSteps = 18;
  const progressPercent = (currentStep / totalSteps) * 100;

  // Resume prompt component
  if (showResumePrompt && savedProgress) {
    return (
      <WizardContainer embedded={embedded} onClose={onClose}>
        <div className="p-6 md:p-8">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-400/20 to-emerald-400/20 flex items-center justify-center">
              <UserCheck className="w-8 h-8 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Welcome Back!</h2>
            <p className="text-white/70 mb-2">
              We found your previous application in progress.
            </p>
            <p className="text-white/60 text-sm mb-6">
              Email: <span className="text-cyan-400">{savedProgress.formData.email}</span>
            </p>
            <div className="flex flex-col gap-3 max-w-sm mx-auto">
              <button
                onClick={handleResumeProgress}
                className="w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all"
              >
                Continue Where I Left Off
              </button>
              <button
                onClick={handleStartFresh}
                className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/20 text-white/70 hover:text-white rounded-xl transition-all"
              >
                Start Fresh Application
              </button>
            </div>
          </div>
        </div>
      </WizardContainer>
    );
  }

  return (
    <WizardContainer embedded={embedded} onClose={handleClose}>
      {!embedded && (
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all"
        >
          <X size={18} />
        </button>
      )}

      {currentStep > STEPS.WELCOME && currentStep < STEPS.COMPLETE && (
        <div className={`${embedded ? '' : 'absolute top-0 left-0 right-0'} h-1 bg-white/10`}>
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      )}

      <div className={`p-6 ${embedded ? '' : 'md:p-8'} overflow-y-auto ${embedded ? 'max-h-[500px]' : 'max-h-[90vh]'}`}>
        <AnimatePresence mode="wait">
          {/* WELCOME */}
          {currentStep === STEPS.WELCOME && (
            <StepContainer key="welcome">
              <div className="text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-400/20 to-emerald-400/20 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-cyan-400" />
                </motion.div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Welcome to Your Path to Funding!</h2>
                <p className="text-white/70 text-sm mb-1">Get qualified for the</p>
                <p className="text-2xl font-bold text-emerald-400 mb-4">$120,000 Business Funding</p>
                <p className="text-white/60 text-xs mb-6">Takes less than 3 minutes</p>
                <button onClick={nextStep} className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white font-semibold rounded-xl flex items-center gap-2 mx-auto transition-all shadow-lg shadow-cyan-500/25">
                  Start Pre-Approval <ChevronRight size={18} />
                </button>
              </div>
            </StepContainer>
          )}

          {/* NAME */}
          {currentStep === STEPS.NAME && (
            <StepContainer key="name">
              <div className="text-center mb-6">
                <User className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">What is your name?</h2>
                <p className="text-white/60 text-sm">We would love to know who we are helping!</p>
              </div>
              <div className="space-y-3 max-w-sm mx-auto">
                <input type="text" placeholder="First Name" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all" autoFocus />
                <input type="text" placeholder="Last Name" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all" />
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.firstName && formData.lastName} />
            </StepContainer>
          )}

          {/* CONTACT */}
          {currentStep === STEPS.CONTACT && (
            <StepContainer key="contact">
              <div className="text-center mb-6">
                <Mail className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">Nice to meet you, {formData.firstName}!</h2>
                <p className="text-white/60 text-sm">How can we reach you?</p>
              </div>
              <div className="space-y-3 max-w-sm mx-auto">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input type="email" placeholder="Email Address" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all" autoFocus />
                </div>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input type="tel" placeholder="Mobile Phone" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all" />
                </div>
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.email && formData.phone} />
            </StepContainer>
          )}

          {/* CATEGORY */}
          {currentStep === STEPS.CATEGORY && (
            <StepContainer key="category">
              <div className="text-center mb-4">
                <Building2 className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">What type of business, {formData.firstName}?</h2>
                <p className="text-white/60 text-sm">Select your category</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-1">
                {BUSINESS_CATEGORIES.map((cat) => (
                  <motion.button key={cat.id} whileTap={{ scale: 0.98 }} onClick={() => handleCategorySelect(cat)} className={`p-2 rounded-lg border transition-all flex flex-col items-center text-center ${formData.category === cat.id ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                    <img src={cat.image} alt={cat.label} className="w-10 h-10 rounded-lg object-cover mb-1" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="text-[10px] text-white/80 leading-tight">{cat.label}</span>
                  </motion.button>
                ))}
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.category} />
            </StepContainer>
          )}

          {/* LOCATION */}
          {currentStep === STEPS.LOCATION && (
            <StepContainer key="location">
              <div className="text-center mb-6">
                <MapPin className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">Where is your business located?</h2>
                <p className="text-white/60 text-sm">We will check availability in your area</p>
              </div>
              <div className="max-w-sm mx-auto">
                <input type="text" placeholder="Enter ZIP Code" value={formData.zipCode} onChange={(e) => handleInputChange('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))} className="w-full px-4 py-4 bg-white/5 border border-white/20 rounded-xl text-white text-xl text-center placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all tracking-widest" maxLength={5} autoFocus />
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.zipCode.length === 5} />
            </StepContainer>
          )}

          {/* CHALLENGE */}
          {currentStep === STEPS.CHALLENGE && (
            <StepContainer key="challenge">
              <div className="text-center mb-4">
                <Target className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">{formData.firstName}, what is your biggest challenge?</h2>
                <p className="text-white/60 text-sm">When it comes to marketing or growth</p>
              </div>
              <div className="grid grid-cols-1 gap-2 max-w-md mx-auto">
                {CHALLENGE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <motion.button key={opt.id} whileTap={{ scale: 0.98 }} onClick={() => handleInputChange('challenge', opt.id)} className={`p-3 rounded-xl border transition-all flex items-center gap-3 text-left ${formData.challenge === opt.id ? 'bg-emerald-500/20 border-emerald-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                      <Icon className={`w-5 h-5 flex-shrink-0 ${formData.challenge === opt.id ? 'text-emerald-400' : 'text-white/40'}`} />
                      <span className="text-sm text-white/80">{opt.label}</span>
                    </motion.button>
                  );
                })}
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.challenge} />
            </StepContainer>
          )}

          {/* WHY NOW */}
          {currentStep === STEPS.WHY_NOW && (
            <StepContainer key="whynow">
              <div className="text-center mb-4">
                <Clock className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">Why is now the right time?</h2>
                <p className="text-white/60 text-sm">What made you take action today?</p>
              </div>
              <div className="space-y-2 max-w-md mx-auto">
                {[
                  { id: 'revenue_down', label: 'Revenue is down, I need to act fast' },
                  { id: 'competition', label: 'Competition is pulling ahead' },
                  { id: 'growth_ready', label: 'I am ready to grow and scale' },
                  { id: 'tired_struggling', label: 'Tired of struggling alone' },
                  { id: 'opportunity', label: 'Saw this opportunity and wanted to explore' },
                ].map((opt) => (
                  <motion.button key={opt.id} whileTap={{ scale: 0.99 }} onClick={() => handleInputChange('whyNow', opt.id)} className={`w-full p-3 rounded-xl border transition-all text-left ${formData.whyNow === opt.id ? 'bg-emerald-500/20 border-emerald-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                    <span className="text-sm text-white/80">{opt.label}</span>
                  </motion.button>
                ))}
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.whyNow} />
            </StepContainer>
          )}

          {/* VISION */}
          {currentStep === STEPS.VISION && (
            <StepContainer key="vision">
              <div className="text-center mb-4">
                <Eye className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">What does success look like?</h2>
                <p className="text-white/60 text-sm">In 6-12 months (select all that apply)</p>
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
                {VISION_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = formData.vision.includes(opt.id);
                  return (
                    <motion.button key={opt.id} whileTap={{ scale: 0.98 }} onClick={() => handleVisionToggle(opt.id)} className={`p-3 rounded-xl border transition-all flex items-center gap-2 text-left ${isSelected ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-cyan-400 bg-cyan-400' : 'border-white/30'}`}>
                        {isSelected && <Check size={12} className="text-black" />}
                      </div>
                      <span className="text-xs text-white/80">{opt.label}</span>
                    </motion.button>
                  );
                })}
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.vision.length > 0} />
            </StepContainer>
          )}

          {/* WHATS MISSING */}
          {currentStep === STEPS.WHATS_MISSING && (
            <StepContainer key="whatsmissing">
              <div className="text-center mb-4">
                <AlertTriangle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">What has been missing?</h2>
                <p className="text-white/60 text-sm">Why have you not achieved this yet?</p>
              </div>
              <div className="space-y-2 max-w-md mx-auto">
                {[
                  { id: 'no_time', label: 'No time to execute everything' },
                  { id: 'no_team', label: 'No team or reliable support' },
                  { id: 'no_budget', label: 'Limited budget for marketing' },
                  { id: 'no_strategy', label: 'Lack of clear strategy' },
                  { id: 'bad_tools', label: 'Using outdated tools/technology' },
                  { id: 'tried_failed', label: 'Tried agencies before and got burned' },
                ].map((opt) => (
                  <motion.button key={opt.id} whileTap={{ scale: 0.99 }} onClick={() => handleInputChange('whatsMissing', opt.id)} className={`w-full p-3 rounded-xl border transition-all text-left ${formData.whatsMissing === opt.id ? 'bg-emerald-500/20 border-emerald-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                    <span className="text-sm text-white/80">{opt.label}</span>
                  </motion.button>
                ))}
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.whatsMissing} />
            </StepContainer>
          )}

          {/* COMMITMENT */}
          {currentStep === STEPS.COMMITMENT && (
            <StepContainer key="commitment">
              <div className="text-center mb-4">
                <Rocket className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">On a scale of 1-10...</h2>
                <p className="text-white/60 text-sm">How ready are you to fix this and grow?</p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 max-w-md mx-auto">
                {[1,2,3,4,5,6,7,8,9,10].map((num) => (
                  <motion.button key={num} whileTap={{ scale: 0.95 }} onClick={() => handleInputChange('commitmentLevel', num)} className={`w-10 h-10 rounded-xl border-2 font-bold text-sm transition-all ${formData.commitmentLevel === num ? (num >= 8 ? 'bg-green-500 border-green-400 text-white' : num >= 5 ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-red-500 border-red-400 text-white') : 'bg-white/5 border-white/20 text-white/60 hover:border-white/40'}`}>
                    {num}
                  </motion.button>
                ))}
              </div>
              {formData.commitmentLevel && formData.commitmentLevel >= 7 && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-green-400 text-sm mt-3">Great! You are ready!</motion.p>
              )}
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.commitmentLevel !== null} />
            </StepContainer>
          )}

          {/* OWNERSHIP */}
          {currentStep === STEPS.OWNERSHIP && (
            <StepContainer key="ownership">
              <div className="text-center mb-6">
                <Users className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">Are you the owner or decision maker?</h2>
              </div>
              <div className="flex gap-3 justify-center max-w-sm mx-auto">
                <motion.button whileTap={{ scale: 0.98 }} onClick={() => handleInputChange('isOwner', true)} className={`flex-1 p-4 rounded-xl border transition-all flex flex-col items-center ${formData.isOwner === true ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  <Check className={`w-8 h-8 mb-2 ${formData.isOwner === true ? 'text-cyan-400' : 'text-white/40'}`} />
                  <span className="text-white font-semibold text-sm">Yes</span>
                </motion.button>
                <motion.button whileTap={{ scale: 0.98 }} onClick={() => handleInputChange('isOwner', false)} className={`flex-1 p-4 rounded-xl border transition-all flex flex-col items-center ${formData.isOwner === false ? 'bg-emerald-500/20 border-emerald-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  <X className={`w-8 h-8 mb-2 ${formData.isOwner === false ? 'text-emerald-400' : 'text-white/40'}`} />
                  <span className="text-white font-semibold text-sm">No</span>
                </motion.button>
              </div>
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.isOwner !== null} />
            </StepContainer>
          )}

          {/* REPRESENTATIVE - NEW */}
          {currentStep === STEPS.REPRESENTATIVE && (
            <StepContainer key="representative">
              <div className="text-center mb-6">
                <Headphones className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">Is a representative helping you?</h2>
                <p className="text-white/60 text-sm">Are you doing this on your own or with assistance?</p>
              </div>
              <div className="flex gap-3 justify-center max-w-sm mx-auto mb-4">
                <motion.button whileTap={{ scale: 0.98 }} onClick={() => handleInputChange('hasRepresentative', false)} className={`flex-1 p-4 rounded-xl border transition-all flex flex-col items-center ${formData.hasRepresentative === false ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  <User className={`w-8 h-8 mb-2 ${formData.hasRepresentative === false ? 'text-cyan-400' : 'text-white/40'}`} />
                  <span className="text-white font-semibold text-sm">On My Own</span>
                </motion.button>
                <motion.button whileTap={{ scale: 0.98 }} onClick={() => handleInputChange('hasRepresentative', true)} className={`flex-1 p-4 rounded-xl border transition-all flex flex-col items-center ${formData.hasRepresentative === true ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  <Headphones className={`w-8 h-8 mb-2 ${formData.hasRepresentative === true ? 'text-cyan-400' : 'text-white/40'}`} />
                  <span className="text-white font-semibold text-sm">With a Rep</span>
                </motion.button>
              </div>
              {formData.hasRepresentative && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="max-w-sm mx-auto">
                  <input type="text" placeholder="Representative's Name" value={formData.representativeName} onChange={(e) => handleInputChange('representativeName', e.target.value)} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all" autoFocus />
                </motion.div>
              )}
              <NavigationButtons onBack={prevStep} onNext={nextStep} canProceed={formData.hasRepresentative !== null && (!formData.hasRepresentative || formData.representativeName)} />
            </StepContainer>
          )}

          {/* REFERRAL */}
          {currentStep === STEPS.REFERRAL && (
            <StepContainer key="referral">
              <div className="text-center mb-6">
                <Gift className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-1">Did anyone refer you?</h2>
              </div>
              <div className="flex gap-3 justify-center max-w-sm mx-auto mb-4">
                <motion.button whileTap={{ scale: 0.98 }} onClick={() => handleInputChange('wasReferred', true)} className={`flex-1 p-3 rounded-xl border transition-all ${formData.wasReferred === true ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  <span className="text-white font-semibold">Yes</span>
                </motion.button>
                <motion.button whileTap={{ scale: 0.98 }} onClick={() => { handleInputChange('wasReferred', false); handleInputChange('referrerName', ''); }} className={`flex-1 p-3 rounded-xl border transition-all ${formData.wasReferred === false ? 'bg-cyan-500/20 border-cyan-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  <span className="text-white font-semibold">No</span>
                </motion.button>
              </div>
              {formData.wasReferred && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="max-w-sm mx-auto">
                  <input type="text" placeholder="Who referred you?" value={formData.referrerName} onChange={(e) => handleInputChange('referrerName', e.target.value)} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none transition-all" autoFocus />
                </motion.div>
              )}
              <NavigationButtons onBack={prevStep} onNext={startVerification} nextLabel="Verify Eligibility" canProceed={formData.wasReferred !== null && (!formData.wasReferred || formData.referrerName)} />
            </StepContainer>
          )}

          {/* VERIFYING */}
          {currentStep === STEPS.VERIFYING && (
            <StepContainer key="verifying">
              <div className="text-center py-4">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="w-20 h-20 mx-auto mb-6">
                  <img src="https://customer-assets.emergentagent.com/job_marketai-hub-17/artifacts/zw4k9iu6_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png" alt="Loading" className="w-full h-full object-contain" />
                </motion.div>
                <h2 className="text-xl font-bold text-white mb-3">Verifying Eligibility...</h2>
                <p className="text-white/60 text-sm mb-4">Checking {formData.categoryLabel} in {formData.zipCode}</p>
                <div className="max-w-sm mx-auto">
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" initial={{ width: 0 }} animate={{ width: `${Math.min(verificationProgress, 100)}%` }} />
                  </div>
                  <p className="text-white/40 text-xs">{Math.min(Math.round(verificationProgress), 100)}%</p>
                </div>
              </div>
            </StepContainer>
          )}

          {/* APPROVED */}
          {currentStep === STEPS.APPROVED && (
            <StepContainer key="approved">
              <div className="text-center py-2">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400/20 to-cyan-400/20 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-400" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-2">Congratulations, {formData.firstName}!</h2>
                <p className="text-xl font-bold text-green-400 mb-4">You Are Pre-Approved!</p>
                <div className="bg-white/5 rounded-xl p-3 max-w-sm mx-auto mb-4">
                  <p className="text-cyan-400 font-semibold text-sm">{formData.categoryLabel}</p>
                  <p className="text-white/70 text-xs">ZIP: {formData.zipCode}</p>
                </div>
                <p className="text-white/60 text-xs mb-4">Next: Secure your spot with a $75 refundable application fee</p>
                <button onClick={() => setCurrentStep(STEPS.APPLICATION_FEE)} className="px-6 py-3 bg-gradient-to-r from-green-500 to-cyan-500 text-white font-semibold rounded-xl flex items-center gap-2 mx-auto transition-all">
                  Continue <ChevronRight size={18} />
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 2: APPLICATION FEE - Only $75 option */}
          {currentStep === STEPS.APPLICATION_FEE && (
            <StepContainer key="application-fee">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-cyan-400 font-bold">2</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 2: Secure Your Spot</h2>
                <p className="text-white/60 text-sm">Pay your refundable application fee</p>
              </div>
              <div className="max-w-sm mx-auto bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70 text-sm">Application Fee</span>
                  <span className="text-2xl font-bold text-white">$75</span>
                </div>
                <div className="space-y-1 text-xs text-white/60 mb-4">
                  <p>✓ 100% refundable if not approved</p>
                  <p>✓ Secures your category</p>
                  <p>✓ Unlocks next steps</p>
                </div>
                <button onClick={() => initiatePayment('application_fee')} disabled={isLoading} className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard size={16} /> Pay $75 Application Fee</>}
                </button>
                <p className="text-white/40 text-xs mt-3 text-center">You'll be redirected to our secure payment page</p>
              </div>
            </StepContainer>
          )}

          {/* STEP 3: BOOK CLOSING CALL */}
          {currentStep === STEPS.BOOK_CALL && (
            <StepContainer key="book-call">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-cyan-400 font-bold">3</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 3: Book Your Closing Call</h2>
                <p className="text-white/60 text-sm">Schedule a call with our funding specialist</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="w-6 h-6 text-emerald-400" />
                    <div>
                      <p className="text-white font-semibold text-sm">Closing Call</p>
                      <p className="text-white/60 text-xs">15-20 minutes • Review your funding options</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => window.open('https://link.oneforcelive.io/widget/bookings/path-to-funding-closing-call', '_blank')}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
                  >
                    <Headphones size={16} /> Book Closing Call
                  </button>
                </div>
                <button onClick={() => setCurrentStep(STEPS.WE_CALL_YOU)} className="w-full py-2 text-white/60 text-sm hover:text-white transition-colors">
                  Skip for now →
                </button>
                <button onClick={() => setCurrentStep(STEPS.APPLICATION_FEE)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 4: WE CALL YOU */}
          {currentStep === STEPS.WE_CALL_YOU && (
            <StepContainer key="we-call-you">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-cyan-400 font-bold">4</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 4: We'll Call You</h2>
                <p className="text-white/60 text-sm">Our team will reach out to confirm your funding</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-400" />
                      <p className="text-white/80 text-sm">Application fee received</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-cyan-400" />
                      <p className="text-white/80 text-sm">We'll call within 24-48 hours</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-emerald-400" />
                      <p className="text-white/80 text-sm">Confirm your funding details</p>
                    </div>
                  </div>
                </div>
                <p className="text-white/50 text-xs text-center mb-4">
                  Don't want to wait? Continue to review the presentation and directory.
                </p>
                <button onClick={() => setCurrentStep(STEPS.REVIEW_PRESENTATION)} className="w-full py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all">
                  Continue <ChevronRight size={16} />
                </button>
                <button onClick={() => setCurrentStep(STEPS.BOOK_CALL)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 5: REVIEW PRESENTATION */}
          {currentStep === STEPS.REVIEW_PRESENTATION && (
            <StepContainer key="review-presentation">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-cyan-400 font-bold">5</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 5: Explore Your Resources</h2>
                <p className="text-white/60 text-sm">Check out the platform, directory, and POD</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="space-y-3 mb-4">
                  <button 
                    onClick={() => window.open('https://aihpower.com', '_blank')}
                    className="w-full p-3 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-emerald-400" />
                      <span className="text-white text-sm">Explore AI Horse-Powered Website</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </button>
                  <button 
                    onClick={() => window.open('https://search.aihorsepowered.com', '_blank')}
                    className="w-full p-3 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-cyan-400" />
                      <span className="text-white text-sm">Explore Directory</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </button>
                  <button 
                    onClick={() => window.open('https://aihorsepowered.com', '_blank')}
                    className="w-full p-3 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <Rocket className="w-5 h-5 text-purple-400" />
                      <span className="text-white text-sm">Preview POD Platform</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </button>
                </div>
                
                {/* Checkbox to confirm review */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={hasReviewedPresentation}
                      onChange={(e) => {
                        setHasReviewedPresentation(e.target.checked);
                        if (e.target.checked) {
                          setStepsUnlocked(true);
                        }
                      }}
                      className="w-5 h-5 mt-0.5 rounded border-white/20 bg-white/10 text-emerald-500 focus:ring-emerald-500"
                    />
                    <span className="text-white/80 text-sm">
                      I have explored the Website, Directory, and POD
                    </span>
                  </label>
                </div>
                
                <button 
                  onClick={() => setCurrentStep(STEPS.FUNDING_OPTIONS)}
                  disabled={!hasReviewedPresentation}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {hasReviewedPresentation ? 'Continue to Funding Options' : 'Please confirm review above'}
                  {hasReviewedPresentation && <ChevronRight size={16} />}
                </button>
                <button onClick={() => setCurrentStep(STEPS.WE_CALL_YOU)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 6: FUNDING OPTIONS */}
          {currentStep === STEPS.FUNDING_OPTIONS && (
            <StepContainer key="funding-options">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-emerald-400 font-bold">6</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 6: Choose Your Funding</h2>
                <p className="text-white/60 text-sm">Select how you want to activate your $120K</p>
              </div>
              
              <div className="space-y-3 max-w-md mx-auto">
                {/* 4% Option */}
                <button 
                  onClick={() => {
                    localStorage.setItem('preApprovalFormData', JSON.stringify(formData));
                    const params = new URLSearchParams({
                      first_name: formData.firstName || '',
                      last_name: formData.lastName || '',
                      email: formData.email || '',
                      phone: formData.phone || '',
                    });
                    window.location.href = `https://link.oneforcelive.io/payment-link/690d0418ac56feeb9ffab328?${params.toString()}`;
                  }}
                  className="w-full p-3 rounded-xl border text-left transition-all relative bg-white/5 border-white/10 hover:bg-green-500/20 hover:border-green-400"
                >
                  <div className="absolute -top-2 -right-2 bg-emerald-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full">BEST VALUE</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-white">4%</span>
                      <span className="text-emerald-400 text-xs ml-2">$4,800 one-time</span>
                    </div>
                    <span className="text-green-400 text-xs">Save $2,400!</span>
                  </div>
                </button>
                
                {/* 5% Option */}
                <button 
                  onClick={() => {
                    localStorage.setItem('preApprovalFormData', JSON.stringify(formData));
                    const params = new URLSearchParams({
                      first_name: formData.firstName || '',
                      last_name: formData.lastName || '',
                      email: formData.email || '',
                      phone: formData.phone || '',
                    });
                    window.location.href = `https://link.oneforcelive.io/payment-link/6995091344f21fe74bd5f960?${params.toString()}`;
                  }}
                  className="w-full p-3 rounded-xl border text-left transition-all bg-white/5 border-white/10 hover:bg-cyan-500/20 hover:border-cyan-400"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-white">5%</span>
                      <span className="text-emerald-400 text-xs ml-2">$2k/mo × 3</span>
                    </div>
                    <span className="text-white/50 text-xs">$6k total</span>
                  </div>
                </button>
                
                {/* 6% Option */}
                <button 
                  onClick={() => {
                    localStorage.setItem('preApprovalFormData', JSON.stringify(formData));
                    const params = new URLSearchParams({
                      first_name: formData.firstName || '',
                      last_name: formData.lastName || '',
                      email: formData.email || '',
                      phone: formData.phone || '',
                    });
                    window.location.href = `https://link.oneforcelive.io/payment-link/699507b4e6b911473ce8c546?${params.toString()}`;
                  }}
                  className="w-full p-3 rounded-xl border text-left transition-all bg-white/5 border-white/10 hover:bg-purple-500/20 hover:border-purple-400"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-white">6%</span>
                      <span className="text-emerald-400 text-xs ml-2">$600/mo × 12</span>
                    </div>
                    <span className="text-white/50 text-xs">$7,200 total</span>
                  </div>
                </button>
              </div>
              <div className="mt-6 max-w-md mx-auto space-y-2">
                <button onClick={() => setCurrentStep(STEPS.VIEW_PRICING)} className="w-full text-center text-white/60 text-xs hover:text-white transition-colors underline">
                  I have paid my funding fee and ready for next step
                </button>
                <button onClick={() => setCurrentStep(STEPS.VIEW_PRICING)} className="w-full text-center text-white/40 text-xs hover:text-white/60 transition-colors underline">
                  I don't want the $120,000 Co-Op Funds and just want to access platform with no co-op support
                </button>
                <button onClick={() => setCurrentStep(STEPS.REVIEW_PRESENTATION)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 7: VIEW PRICING */}
          {currentStep === STEPS.VIEW_PRICING && (
            <StepContainer key="view-pricing">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-cyan-400 font-bold">7</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 7: View Pricing Details</h2>
                <p className="text-white/60 text-sm">See all pricing options and packages</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                  <DollarSign className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <p className="text-white/80 text-sm text-center mb-4">
                    View complete pricing breakdown, package comparisons, and ROI calculator.
                  </p>
                  <button 
                    onClick={() => window.open('https://aihpower.com/pricing', '_blank')}
                    className="w-full py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
                  >
                    View Full Pricing <ChevronRight size={16} />
                  </button>
                </div>
                <button onClick={() => setCurrentStep(STEPS.COMPLETE_ONBOARDING)} className="w-full py-2 text-white/60 text-sm hover:text-white transition-colors">
                  Continue to Onboarding →
                </button>
                <button onClick={() => setCurrentStep(STEPS.FUNDING_OPTIONS)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 8: COMPLETE ONBOARDING */}
          {currentStep === STEPS.COMPLETE_ONBOARDING && (
            <StepContainer key="complete-onboarding">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-emerald-400 font-bold">8</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 8: Onboarding Call Scheduled?</h2>
                <p className="text-white/60 text-sm">Your account rep will schedule your onboarding call</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                  <Calendar className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <p className="text-white/80 text-sm text-center mb-4">
                    Has your account representative scheduled your onboarding call?
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10 cursor-pointer hover:bg-white/10 transition-all">
                      <input 
                        type="radio" 
                        name="onboardingScheduled"
                        className="w-5 h-5 text-emerald-500 focus:ring-emerald-500"
                        onChange={() => {}}
                      />
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-400" />
                        <span className="text-white text-sm">Yes, scheduled</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10 cursor-pointer hover:bg-white/10 transition-all">
                      <input 
                        type="radio" 
                        name="onboardingScheduled"
                        className="w-5 h-5 text-emerald-500 focus:ring-emerald-500"
                        onChange={() => {}}
                      />
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-yellow-400" />
                        <span className="text-white text-sm">No, not yet</span>
                      </div>
                    </label>
                  </div>
                </div>
                <button onClick={() => setCurrentStep(STEPS.LOGIN_INFO)} className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all">
                  Continue <ChevronRight size={16} />
                </button>
                <button onClick={() => setCurrentStep(STEPS.VIEW_PRICING)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 9: LOGIN INFO */}
          {currentStep === STEPS.LOGIN_INFO && (
            <StepContainer key="login-info">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-cyan-400 font-bold">9</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 9: Login Information</h2>
                <p className="text-white/60 text-sm">Your login details are on the way!</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                  <Mail className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                  <p className="text-white font-semibold text-center mb-3">Login Instructions Coming Soon!</p>
                  <div className="space-y-3 text-sm mb-4">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 mt-0.5" />
                      <p className="text-white/80">Login credentials will be sent to your email</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 mt-0.5" />
                      <p className="text-white/80">Check {formData.email || 'your inbox'} (and spam folder)</p>
                    </div>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-3">
                    <p className="text-white/90 text-sm text-center">
                      <span className="font-semibold text-emerald-400">Tip:</span> Visit our website and go to the <span className="font-semibold">"Access"</span> menu to find your platform login links.
                    </p>
                  </div>
                  <button 
                    onClick={() => window.open('https://aihpower.com', '_blank')}
                    className="w-full py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
                  >
                    <Globe size={16} /> Visit aihpower.com
                  </button>
                </div>
                <button onClick={() => setCurrentStep(STEPS.PLACE_ORDER)} className="w-full py-2 text-white/60 text-sm hover:text-white transition-colors">
                  Continue →
                </button>
                <button onClick={() => setCurrentStep(STEPS.COMPLETE_ONBOARDING)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* STEP 10: ACCEPT INVITES & GET STARTED */}
          {currentStep === STEPS.PLACE_ORDER && (
            <StepContainer key="place-order">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-400/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-emerald-400 font-bold">10</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Step 10: Accept Your Invites</h2>
                <p className="text-white/60 text-sm">Get ready to start working with your team!</p>
              </div>
              <div className="max-w-sm mx-auto">
                <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-xl p-4 border border-emerald-400/30 mb-4">
                  <Users className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <p className="text-white font-semibold text-center mb-3">Accept All Platform Invites</p>
                  <p className="text-white/70 text-sm text-center mb-4">
                    During or before your onboarding call, accept all invites so you can start:
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-3 bg-white/5 p-2 rounded-lg">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-white/80 text-sm">Making orders</span>
                    </div>
                    <div className="flex items-center gap-3 bg-white/5 p-2 rounded-lg">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-white/80 text-sm">Creating tasks</span>
                    </div>
                    <div className="flex items-center gap-3 bg-white/5 p-2 rounded-lg">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-white/80 text-sm">Collaborating with your team</span>
                    </div>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                    <p className="text-white/90 text-xs text-center">
                      <span className="font-semibold text-yellow-400">Important:</span> Check your email for workspace and platform invites. Accept them all to get full access!
                    </p>
                  </div>
                </div>
                <button onClick={() => setCurrentStep(STEPS.COMPLETE)} className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all">
                  <Sparkles size={16} /> Complete Setup
                </button>
                <button onClick={() => setCurrentStep(STEPS.LOGIN_INFO)} className="w-full mt-2 py-1 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
            </StepContainer>
          )}

          {/* COMPLETE */}
          {currentStep === STEPS.COMPLETE && (
            <StepContainer key="complete">
              <div className="text-center py-2">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400/20 to-cyan-400/20 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-400" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-2">You're All Set!</h2>
                <p className="text-lg text-green-400 font-semibold mb-4">Welcome to One Force Live!</p>
                <div className="bg-white/5 rounded-xl p-4 max-w-sm mx-auto mb-4">
                  <p className="text-white/80 text-sm mb-3">Quick Links:</p>
                  <div className="space-y-2">
                    <button 
                      onClick={() => window.open('https://app.oneforcelive.io', '_blank')}
                      className="w-full py-2 bg-white/10 text-white text-sm rounded-lg hover:bg-white/20 transition-all"
                    >
                      Login to POD
                    </button>
                    <button 
                      onClick={() => window.open('https://aihorsepowered.com/marketplace', '_blank')}
                      className="w-full py-2 bg-white/10 text-white text-sm rounded-lg hover:bg-white/20 transition-all"
                    >
                      Visit Marketplace
                    </button>
                    <button 
                      onClick={() => window.open('https://search.aihorsepowered.com', '_blank')}
                      className="w-full py-2 bg-white/10 text-white text-sm rounded-lg hover:bg-white/20 transition-all"
                    >
                      Explore Directory
                    </button>
                  </div>
                </div>
                <button onClick={handleComplete} className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl transition-all">
                  Close
                </button>
              </div>
            </StepContainer>
          )}

        </AnimatePresence>
      </div>
    </WizardContainer>
  );
};

// Wrapper component for modal vs embedded mode
const WizardContainer = ({ embedded, onClose, children }) => {
  if (embedded) {
    return (
      <div className="bg-gradient-to-br from-[#0d1117] to-[#161b22] rounded-2xl border border-cyan-500/30 shadow-xl overflow-hidden">
        {children}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl max-h-[90vh] bg-gradient-to-br from-[#0d1117] to-[#161b22] rounded-2xl border border-cyan-500/30 shadow-2xl overflow-hidden"
      >
        {children}
      </motion.div>
    </div>
  );
};

const StepContainer = ({ children }) => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>{children}</motion.div>
);

const NavigationButtons = ({ onBack, onNext, nextLabel = 'Continue', canProceed = true }) => (
  <div className="flex gap-3 justify-center mt-6">
    {onBack && (
      <button onClick={onBack} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white text-sm rounded-xl flex items-center gap-1 transition-all">
        <ChevronLeft size={16} /> Back
      </button>
    )}
    <button onClick={onNext} disabled={!canProceed} className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white font-semibold text-sm rounded-xl flex items-center gap-1 transition-all disabled:opacity-50">
      {nextLabel} <ChevronRight size={16} />
    </button>
  </div>
);

export default PreApprovalWizard;
